import { defineMessages } from "react-intl"
import { BulkActionDefinition, BulkActionType, ColumnDefinition, ColumnDefinitionCategory, DataType, TotalType } from "."
import { ObjectType, ObjectTypeConfig } from ".."
import { BULK_OPERATION_TYPE } from "../../../../utils/automation/bulk"
import { PREFERENCE_TYPE } from "../../constants"

export enum OrdersColumnIds {
    IDENTIFIER = "identifier",
    LOCATION_NAME = "locationName",
    PROJECT_TITLE = "projectTitle",
    CONTACT = "contact",
    ORDERED_AT_DATE = "orderedAt",
    STATUS = "customStatusName",
    TOTAL_AMOUNT = "totalAmount",
    ARCHIVED = "isArchived",
    PO_NUMBER = "poNumber",
    PURCHASE_ORDER = "poExternalIdAnalyzed",
    CUSTOMER_TYPE = "customerType",
    CREATED_AT_DATE = "createdAt",
    UPDATED_AT_DATE = "updatedAt",
    CONTACT_NAME = "contactName",
    CONTACT_FIRST_NAME = "contactFirstName",
    CONTACT_LAST_NAME = "contactLastName",
    CONTACT_ORGANIZATION_NAME = "contactOrganizationName",
    CONTACT_TYPE = "contactType",
    CONTACT_PHONE_NUMBER = "contactPhoneNumber",
    CONTACT_TAX_NUMBER = "contactTaxNumber",
    CONTACT_EMAIL = "contactEmail",
    PRODUCTS_TITLE = "productsTitle",
    PRODUCTS_SKU = "productsSku",
    PRODUCTS_LABELS = "productsLabels",
    TAX_ID = "taxId",
    PAYMENT_TRANSACTIONS_TYPE = "paymentTransactionsType",
    PAYMENT_TRANSACTIONS_AMOUNT = "paymentTransactionsAmount",
    BALANCE = "balance",
    TAX_AMOUNT = "totalTaxAmount",
}

const columnDefinitions: ColumnDefinition[] = [
    {
        id: OrdersColumnIds.IDENTIFIER,
        sortId: "identifierInteger",
        defaultSort: true,
        dataType: DataType.LINK,
        dataAttributes: {
            redirect: "/orders/{id}/edit",
            secondaryValueKey: OrdersColumnIds.PO_NUMBER,
        },
        totalAttributes: {
            key: "totalItemsCount",
            type: TotalType.STRING,
        },
        sortable: true,
        identifier: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: OrdersColumnIds.LOCATION_NAME,
        sortId: "locationNameSort",
        dataType: DataType.LOCATION,
        dataAttributes: {
            redirect: "/locations/{locationId}/edit"
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: OrdersColumnIds.PROJECT_TITLE,
        sortId: "projectTitleSort",
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: OrdersColumnIds.CONTACT,
        accessorFn: row => row.contact?.name,
        sortId: "contact.nameSort",
        dataType: DataType.CONTACT,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: OrdersColumnIds.ORDERED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: OrdersColumnIds.STATUS,
        dataType: DataType.BADGE,
        dataAttributes: {
            statusCategoryKey: "status",
            badgeConfigMap: {
                "draft": {
                    message: {
                        id: "generic-listing.orders.badge.draft",
                        defaultMessage: "Draft",
                    },
                    type: "primary",
                },
                "ordered": {
                    message: {
                        id: "generic-listing.orders.badge.ordered",
                        defaultMessage: "Ordered",
                    },
                    type: "warning",
                },
                "completed": {
                    message: {
                        id: "generic-listing.orders.badge.complete",
                        defaultMessage: "Completed",
                    },
                    type: "success",
                },
            },
            textAlign: "right",
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: OrdersColumnIds.TOTAL_AMOUNT,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            showPaidAmount: true,
            paidAmountKey: "paidAmount",
            textAlign: "right",
        },
        totalAttributes: {
            key: "totals.baseCurrencyTotalAmount",
            type: TotalType.CURRENCY,
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: OrdersColumnIds.ARCHIVED,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: OrdersColumnIds.PO_NUMBER,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: OrdersColumnIds.PURCHASE_ORDER,
        dataType: DataType.STRING,
        sortable: false,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: OrdersColumnIds.CUSTOMER_TYPE,
        sortId: "contact.customerTypeName",
        accessorFn: row => row.contact?.customerTypeName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: OrdersColumnIds.CREATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: OrdersColumnIds.UPDATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: OrdersColumnIds.CONTACT_NAME,
        sortId: "contact.nameSort",
        accessorFn: row => row.contact?.name,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: OrdersColumnIds.CONTACT_FIRST_NAME,
        sortId: "contact.firstNameKeyword",
        accessorFn: row => row.contact?.firstName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: OrdersColumnIds.CONTACT_LAST_NAME,
        sortId: "contact.lastNameKeyword",
        accessorFn: row => row.contact?.lastName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: OrdersColumnIds.CONTACT_ORGANIZATION_NAME,
        sortId: "contact.organizationNameSort",
        accessorFn: row => row.contact?.organizationName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: OrdersColumnIds.CONTACT_TYPE,
        accessorFn: row => row.contact?.type,
        dataType: DataType.STRING,
        dataAttributes: {
            messageMap: {
                "persons": {
                    id: "generic-listing.contact-type.people",
                    defaultMessage: "People",
                },
                "organizations": {
                    id: "generic-listing.contact-type.organization",
                    defaultMessage: "Organization",
                },
                "suppliers": {
                    id: "generic-listing.contact-type.supplier",
                    defaultMessage: "Supplier",
                },
            }
        },
        sortable: false,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: OrdersColumnIds.CONTACT_PHONE_NUMBER,
        sortId: "contact.phoneNumberKeyword",
        accessorFn: row => row.contact?.phoneNumber,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: OrdersColumnIds.CONTACT_TAX_NUMBER,
        sortId: "contact.taxNumber",
        accessorFn: row => row.contact?.taxNumber,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: OrdersColumnIds.CONTACT_EMAIL,
        sortId: "contact.emailKeyword",
        accessorFn: row => row.contact?.email,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: OrdersColumnIds.PRODUCTS_TITLE,
        accessorKey: "rows",
        sortId: "rowsObject.title",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "title",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: OrdersColumnIds.PRODUCTS_SKU,
        accessorKey: "rows",
        sortId: "rowsObject.sku",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "sku",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: OrdersColumnIds.PRODUCTS_LABELS,
        accessorKey: "rows",
        sortId: "rowsObject.labelIds",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "labelIds",
            optionsKey: "labelOptions",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: OrdersColumnIds.TAX_ID,
        dataType: DataType.OPTIONS,
        dataAttributes: {
            optionsKey: "taxOptions",
            loadingKey: "loadingTaxOptions",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: OrdersColumnIds.TAX_AMOUNT,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: OrdersColumnIds.PAYMENT_TRANSACTIONS_TYPE,
        accessorKey: "paymentTransactions",
        sortId: "paymentTransactionsObject.paymentType",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "paymentTypeName",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: OrdersColumnIds.PAYMENT_TRANSACTIONS_AMOUNT,
        accessorKey: "paymentTransactions",
        sortId: "paymentTransactionsObject.amount",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "amount",
            textAlign: "right",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: OrdersColumnIds.BALANCE,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            textAlign: "right",
        },
        totalAttributes: {
            key: "totals.balance",
            type: TotalType.CURRENCY,
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
]

const headerMessages = defineMessages({
    [OrdersColumnIds.IDENTIFIER]: {
        id: "generic-listing.orders.table-header.identifier",
        defaultMessage: "ID",
    },
    [OrdersColumnIds.LOCATION_NAME]: {
        id: "generic-listing.orders.table-header.location-name",
        defaultMessage: "Location",
    },
    [OrdersColumnIds.PROJECT_TITLE]: {
        id: "generic-listing.orders.table-header.projectTitle",
        defaultMessage: "Title",
    },
    [OrdersColumnIds.CONTACT]: {
        id: "generic-listing.orders.table-header.contact",
        defaultMessage: "Contact",
    },
    [OrdersColumnIds.ORDERED_AT_DATE]: {
        id: "generic-listing.orders.table-header.ordered-at-date",
        defaultMessage: "Ordered at",
    },
    [OrdersColumnIds.STATUS]: {
        id: "generic-listing.orders.table-header.status",
        defaultMessage: "Status",
    },
    [OrdersColumnIds.TOTAL_AMOUNT]: {
        id: "generic-listing.orders.table-header.total-amount",
        defaultMessage: "Total amount",
    },
    [OrdersColumnIds.ARCHIVED]: {
        id: "generic-listing.orders.table-header.archived",
        defaultMessage: "Archived",
    },
    [OrdersColumnIds.PO_NUMBER]: {
        id: "generic-listing.orders.table-header.po-number",
        defaultMessage: "PO Number",
    },
    [OrdersColumnIds.PURCHASE_ORDER]: {
        id: "generic-listing.orders.table-header.purchase-order",
        defaultMessage: "Purchase Order",
    },
    [OrdersColumnIds.CUSTOMER_TYPE]: {
        id: "generic-listing.orders.table-header.customer-type",
        defaultMessage: "Customer Type",
    },
    [OrdersColumnIds.CREATED_AT_DATE]: {
        id: "generic-listing.orders.table-header.created-at-date",
        defaultMessage: "Created At Date",
    },
    [OrdersColumnIds.UPDATED_AT_DATE]: {
        id: "generic-listing.orders.table-header.update-at-date",
        defaultMessage: "Updated At Date",
    },
    [OrdersColumnIds.CONTACT_NAME]: {
        id: "generic-listing.orders.table-header.contact-name",
        defaultMessage: "Contact Name",
    },
    [OrdersColumnIds.CONTACT_FIRST_NAME]: {
        id: "generic-listing.orders.table-header.contact-first-name",
        defaultMessage: "Contact First Name",
    },
    [OrdersColumnIds.CONTACT_LAST_NAME]: {
        id: "generic-listing.orders.table-header.contact-last-name",
        defaultMessage: "Contact Last Name",
    },
    [OrdersColumnIds.CONTACT_ORGANIZATION_NAME]: {
        id: "generic-listing.orders.table-header.contact-organization-name",
        defaultMessage: "Contact Organization",
    },
    [OrdersColumnIds.CONTACT_TYPE]: {
        id: "generic-listing.orders.table-header.contact-type",
        defaultMessage: "Contact Type",
    },
    [OrdersColumnIds.CONTACT_PHONE_NUMBER]: {
        id: "generic-listing.orders.table-header.contact-phone_number",
        defaultMessage: "Contact Phone Number",
    },
    [OrdersColumnIds.CONTACT_TAX_NUMBER]: {
        id: "generic-listing.orders.table-header.contact-tax-number",
        defaultMessage: "Contact Tax Number",
    },
    [OrdersColumnIds.CONTACT_EMAIL]: {
        id: "generic-listing.orders.table-header.contact-email",
        defaultMessage: "Contact Email",
    },
    [OrdersColumnIds.PRODUCTS_TITLE]: {
        id: "generic-listing.orders.table-header.product-title",
        defaultMessage: "Products Title",
    },
    [OrdersColumnIds.PRODUCTS_SKU]: {
        id: "generic-listing.orders.table-header.product-sku",
        defaultMessage: "Products SKU",
    },
    [OrdersColumnIds.PRODUCTS_LABELS]: {
        id: "generic-listing.orders.table-header.product-labels",
        defaultMessage: "Products Labels",
    },
    [OrdersColumnIds.TAX_AMOUNT]: {
        id: "generic-listing.orders.table-header.total-tax-amount",
        defaultMessage: "Tax Amount",
    },
    [OrdersColumnIds.TAX_ID]: {
        id: "generic-listing.orders.table-header.tax-id",
        defaultMessage: "Tax",
    },
    [OrdersColumnIds.PAYMENT_TRANSACTIONS_TYPE]: {
        id: "generic-listing.orders.table-header.payment-transactions-type",
        defaultMessage: "Payment Transactions Type",
    },
    [OrdersColumnIds.PAYMENT_TRANSACTIONS_AMOUNT]: {
        id: "generic-listing.orders.table-header.payment-transactions-amount",
        defaultMessage: "Payment Transactions Amount",
    },
    [OrdersColumnIds.BALANCE]: {
        id: "generic-listing.orders.table-header.balance",
        defaultMessage: "Balance",
    },
})

const bulkActionDefinitions: BulkActionDefinition[] = [
    {
        id: BULK_OPERATION_TYPE.ORDER_STATUS_UPDATE,
        message: {
            id: "generic-listing.bulk-actions.order-status-update",
            defaultMessage: "Change order status",
        },
        icon: "exchange-alt",
    },
    {
        id: BULK_OPERATION_TYPE.SEND_EMAIL,
        message: {
            id: "generic-listing.bulk-actions.send-bulk-email",
            defaultMessage: "Send email",
        },
        icon: "envelope",
    },
    {
        id: BULK_OPERATION_TYPE.DELETE,
        message: {
            id: "generic-listing.bulk-actions.delete-orders",
            defaultMessage: "Delete order(s)",
        },
        icon: "trash",
        type: BulkActionType.DANGER,
    },
]

const configuration: ObjectTypeConfig = {
    objectType: ObjectType.ORDERS,
    columnDefinitions,
    headerMessages,
    listingPreferencesKey: PREFERENCE_TYPE.LISTING_ORDERS,
    searchPlaceholder: {
        id: "generic-listing.orders.search-placeholder",
        defaultMessage: "Search an order...",
    },
    displayString: {
        id: "generic-listing.orders.display-string",
        defaultMessage: "Orders",
    },
    searchObjectType: ObjectType.ORDERS,
    bulkActionDefinitions,
    creationButtonComponent: require("../../../../common/listing/generic-listing/creation-buttons/OrderCreationButton"),
}

export default configuration



// WEBPACK FOOTER //
// ./src/state/listing/generic-listing/column-definitions/orders.ts